import { useExternalSystems } from '../useExternalSystems/useExternalSystems';
import { useVenues } from '../useVenues/useVenues';
import { getVenuesToPublishTo } from '../useExternalSystemPublishing/getVenuesToPublishTo/getVenuesToPublishTo';
import { IPublishDestination } from 'src/components/PublishModal/types';
import { IPublication } from 'src/types/menu.types';
import { canReceiveMenuSet } from './canReceiveMenuSet';

/**
 * One destination per extension instance that can receive a Menu Set, as `useTimetablePublishDestinations`
 * lists them, since the Menu Set publish request addresses instances. A Menu Set reaches the connector as
 * one ordinary menu, so eligibility is the `export_menu` check in `canReceiveMenuSet`. Unlike timetables, venues are
 * filtered to trial or non-trial and decorated with the Menu Set's last publication status, as for menus.
 */
export const useMenuSetPublishDestinations = (isTrial = false, publication?: IPublication) => {
  const venuesQuery = useVenues({ enabled: true });
  const externalSystemsQuery = useExternalSystems();

  const isLoading = venuesQuery.isLoading || externalSystemsQuery.isLoading;
  const isError = venuesQuery.isError || externalSystemsQuery.isError;

  if (isLoading || isError) {
    return { data: [] as IPublishDestination[], isLoading, isError };
  }

  const data: IPublishDestination[] = (externalSystemsQuery.data ?? []).flatMap((externalSystem) => {
    const aggregator = publication?.aggregators.find(
      (publishedTo) => publishedTo.aggregatorId === externalSystem.aggregatorId,
    );
    const venues = getVenuesToPublishTo(venuesQuery.data ?? [], externalSystem.aggregatorId, aggregator, isTrial);
    if (venues.length === 0) return [];

    return externalSystem.configuredAddons.filter(canReceiveMenuSet).map(
      (instance): IPublishDestination => ({
        destination_id: instance.instanceId,
        channel_id: externalSystem.aggregatorId,
        extensionInstanceId: instance.instanceId,
        name: `${instance.addonName} - ${instance.instanceName}`,
        ...(aggregator && {
          publicationStatus: aggregator.publicationStatus,
          publicationDate: aggregator.publicationDate,
        }),
        venues,
      }),
    );
  });

  return { data, isLoading, isError };
};
